import { useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { PageLayout } from "./PageLayout";
import { projects } from "@/data/projects";
import type { Project } from "@/data/projects";
import { track } from "@/lib/analytics";
import bimanualVla from "@/illustrations/bimanual-vla.png";
import atlas from "@/illustrations/atlas.png";
import deepracer from "@/illustrations/deepracer.png";
import real2simMobileManipulator from "@/illustrations/real2sim-mobile-manipulator.png";
import robopainter from "@/illustrations/robopainter.png";
import cncRobotCell from "@/illustrations/cnc-robot-cell.png";
import openRoboticMetaverse from "@/illustrations/open-robotic-metaverse.png";
import applePicker from "@/illustrations/apple-picker.png";
import tanitrone from "@/illustrations/tanitrone.png";
import jetsonMobileRobot from "@/illustrations/jetson-mobile-robot.png";
import roboticsCollective from "@/illustrations/robotics-collective.png";

const illustrations: Record<string, string> = {
  "bimanual-vla": bimanualVla,
  atlas,
  deepracer,
  "real2sim-mobile-manipulator": real2simMobileManipulator,
  robopainter,
  "cnc-robot-cell": cncRobotCell,
  "open-robotic-metaverse": openRoboticMetaverse,
  "apple-picker": applePicker,
  tanitrone,
  "jetson-mobile-robot": jetsonMobileRobot,
  "robotics-collective": roboticsCollective,
};

function ProjectCard({ project }: { project: Project }) {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const node = ref.current;
    if (!node || !("IntersectionObserver" in window)) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let sent = false;
    const observer = new IntersectionObserver(([entry]) => {
      clearTimeout(timer);
      if (entry?.isIntersecting && !sent) {
        timer = setTimeout(() => {
          if (document.visibilityState !== "visible") return;
          sent = track("project_impression", { project_id: project.id, placement: "projects" });
        }, 1000);
      }
    }, { threshold: 0.5 });
    observer.observe(node);
    const reconsider = () => { clearTimeout(timer); observer.unobserve(node); observer.observe(node); };
    window.addEventListener("analytics-choice", reconsider);
    document.addEventListener("visibilitychange", reconsider);
    return () => {
      observer.disconnect(); clearTimeout(timer);
      window.removeEventListener("analytics-choice", reconsider);
      document.removeEventListener("visibilitychange", reconsider);
    };
  }, [project.id]);
  return (
    <li ref={ref} className="project-card">
      <Link viewTransition to={`/projects/${project.id}`} onClick={() => track("project_open", { project_id: project.id, category: project.category, placement: "projects" })}>
        <div className="project-card-body">
          {illustrations[project.id] && (
            <img src={illustrations[project.id]} alt="" className="project-illustration" />
          )}
          <div>
            <h2>{project.title}</h2>
            <div className="project-meta"><span>{project.organization}</span><span>{project.period}</span></div>
            <p>{project.description}</p>
            <ul className="skill-tags" aria-label="Technical skills">
              {project.details.stack.map(skill => <li key={skill}>{skill}</li>)}
            </ul>
          </div>
        </div>
      </Link>
    </li>
  );
}

export function ContactBlock({ source }: { source: string }) {
  return (
    <p className="project-contact">
      Want to know more?{" "}
      <a href="mailto:karim@roboticscollective.org" onClick={() => track("contact_click", { source, channel: "email" })}>Get in touch.</a>
    </p>
  );
}

export function Projects() {
  return (
    <PageLayout>
      <header className="portfolio-intro">
        <h1>Projects</h1>
        <p>A selection of my work in robotics, learning, and simulation.</p>
      </header>
      <ul className="project-list">{projects.map(p => <ProjectCard key={p.id} project={p} />)}</ul>
      <ContactBlock source="projects" />
    </PageLayout>
  );
}

export function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const project = projects.find(p => p.id === slug);
  if (!project) return <PageLayout backTo="/projects" backLabel="← All projects"><h1>Project not found</h1><p><Link viewTransition to="/projects">Explore all projects →</Link></p></PageLayout>;
  return (
    <PageLayout backTo="/projects" backLabel="← All projects">
      <header className="detail-intro">
        {illustrations[project.id] && (
          <img src={illustrations[project.id]} alt="" className="project-illustration project-illustration-large" />
        )}
        <h1>{project.title}</h1>
        <p className="detail-organization">{project.organization} · {project.period}</p>
        <ul className="skill-tags" aria-label="Technical skills">
          {project.details.stack.map(skill => <li key={skill}>{skill}</li>)}
        </ul>
      </header>
      <section className="detail-contributions" aria-label="Project overview and contributions">
        <p>{project.details.intro}</p>
        <ul>{project.details.bullets.map(b => <li key={b}>{b}</li>)}</ul>
      </section>
      <ContactBlock source={project.id} />
    </PageLayout>
  );
}
